import { KeyboardEvent, useRef, useState } from 'react';
import { X } from 'react-bootstrap-icons';

import { parseReturnCodeTokens } from './autoRestartView';

interface Props {
  readonly codes: readonly number[];
  readonly onChange: (codes: readonly number[]) => void;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  /**
   * Placeholder shown when there are no chips yet. Defaults to the auto-restart empty-state copy
   * ("Any failure — …"); callers that require a non-empty set (output matchers) should override it.
   */
  readonly emptyPlaceholder?: string;
}

const DEFAULT_EMPTY_PLACEHOLDER = 'Any failure — type a code, press Enter';

/**
 * A chips input for integer return codes (negatives allowed). The committed codes live in the model; only the uncommitted
 * text buffer is local state. Enter or comma commits the typed value(s); Backspace on an empty buffer removes the last chip;
 * blur commits a pending value. New codes are appended (order preserved) and de-duplicated against the existing list — the
 * backend also dedupes, but doing it here keeps the visible chips honest as you type.
 */
export const ReturnCodeChips = ({ codes, onChange, disabled = false, ariaLabel, emptyPlaceholder = DEFAULT_EMPTY_PLACEHOLDER }: Props) => {
  const [buffer, setBuffer] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const parsed = parseReturnCodeTokens(raw);
    setBuffer('');
    if (parsed.length === 0) return;
    const merged = [...codes];
    for (const code of parsed) {
      if (!merged.includes(code)) merged.push(code);
    }
    if (merged.length !== codes.length) onChange(merged);
  };

  const removeAt = (index: number) => onChange(codes.filter((_, i) => i !== index));

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(buffer);
    } else if (e.key === 'Backspace' && buffer === '' && codes.length > 0) {
      onChange(codes.slice(0, -1));
    }
  };

  return (
    <div className={`return-code-chips${disabled ? ' return-code-chips--disabled' : ''}`} onClick={() => inputRef.current?.focus()} role="presentation">
      {codes.map((code, index) => (
        <span className="return-code-chips__chip" key={`${code}-${index}`}>
          <span className="return-code-chips__value">{code}</span>
          <button
            type="button"
            className="return-code-chips__remove"
            aria-label={`Remove return code ${code}`}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              removeAt(index);
            }}
          >
            <X aria-hidden />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="return-code-chips__input"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-label={ariaLabel ?? 'Add a return code'}
        placeholder={codes.length === 0 ? emptyPlaceholder : ''}
        value={buffer}
        disabled={disabled}
        onChange={(e) => setBuffer(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commit(buffer)}
      />
    </div>
  );
};
